<template>
  <transition name="fade">
    <div
      :class="['zg-loader', {
        [`zg-loader--color-${color}`]: color,
        'zg-loader--boxed': boxed && fullscreen,
        'zg-loader--fullscreen': fullscreen,
        'zg-loader--button': button
      }]"
    >
      <div
        :class="[
          'zg-loader__box',
          `zg-loader--${size}`
        ]"
      >
        <p
          v-if="text"
          class="zg-loader__text"
        >
          {{ text }}
        </p>

        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="25 25 50 50"
          class="zg-loader__circles"
        >
          <circle
            cx="50"
            cy="50"
            :r="button ? 21 : 23"
            fill="none"
            stroke-miterlimit="10"
          />

          <circle
            class="circle-animation"
            cx="50"
            cy="50"
            :r="button ? 21 : 23"
            fill="none"
            stroke-miterlimit="10"
          />
        </svg>
      </div>
    </div>
  </transition>
</template>

<script>
  export default {
    name: 'ZgLoader',
    props: {
      color: {
        type: String,
        required: false,
        default: 'brand-500',
        validator: (val) => {
          return [
            'brand-500',
            'freja',
            'bankid-se',
            'bankid-no',
            'bankid-fi'
          ].includes(val)
        }
      },
      /**
       * Boxed version
       */
      boxed: {
        type: Boolean,
        required: false,
        default: false
      },
      /**
       * When used in a button
       */
      button: {
        type: Boolean,
        required: false,
        default: false
      },
      /**
       * Text shown with the loader
       */
      text: {
        type: String,
        required: false,
        default: null
      },
      /**
       * Make fullscreen
       */
      fullscreen: {
        type: Boolean,
        required: false,
        default: false
      },
      /**
       * small, medium or large affects radius and stroke width.
       */
      size: {
        type: String,
        required: false,
        default: 'large',
        validator: (val) => {
          return ['small', 'medium', 'large', 'xlarge'].indexOf(val) !== -1
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  $circleSizes: (
    small: rem(30px),
    medium: rem(44px),
    large: rem(52px),
    xlarge: rem(62px)
  );

  $circleSizesButton: (
    small: rem(16px),
    medium: rem(20px),
    xlarge: rem(24px)
  );

  .zg-loader {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    &__box {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column-reverse;
      margin: 0 auto;
    }

    &__text {
      color: color(gray-500);
      text-align: center;
      white-space: nowrap;
      margin: rhythm(small) 0 0;
    }

    &__circles {
      animation: rotate 2s linear infinite;
      transform-origin: center center;
    }
  }

  // Circle
  .circle-animation {
    stroke: color(brand-500);
    fill: transparent;
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
    animation: circle 1.5s ease-in-out infinite;
    stroke-linecap: round;
  }

  // Color
  .zg-loader--color-brand-500 {
    .zg-loader__circles {
      .circle-animation {
        stroke: color(brand-500);
      }
    }
  }

  .zg-loader--color-freja {
    .zg-loader__circles {
      .circle-animation {
        stroke: #3e3a93;
      }
    }
  }

  .zg-loader--color-bankid-se {
    .zg-loader__circles {
      .circle-animation {
        stroke: #193e4f;
      }
    }
  }

  .zg-loader--color-bankid-no {
    .zg-loader__circles {
      .circle-animation {
        stroke: #5081be;
      }
    }
  }

  .zg-loader--color-bankid-fi {
    .zg-loader__circles {
      .circle-animation {
        stroke: #17b0e4;
      }
    }
  }

  // Sizes
  .zg-loader {
    &--small {
      .zg-loader {
        &__circles {
          width: map-get($circleSizes, small);
          height: map-get($circleSizes, small);
        }

        &__text {
          @include type-body-s;

          margin: rhythm(small) 0 0;
        }
      }

      circle {
        stroke-width: 5;
      }
    }

    &--medium {
      .zg-loader {
        &__circles {
          width: map-get($circleSizes, medium);
          height: map-get($circleSizes, medium);
        }

        &__text {
          @include type-body-s;

          margin: rhythm(small) 0 0;
        }
      }

      circle {
        stroke-width: 6;
      }
    }

    &--large {
      .zg-loader {
        &__circles {
          width: map-get($circleSizes, large);
          height: map-get($circleSizes, large);
        }

        &__text {
          @include type-body-l;

          margin: rhythm(small) 0 0;
        }
      }

      circle {
        stroke-width: 6;
      }
    }

    &--xlarge {
      .zg-loader {
        &__circles {
          width: map-get($circleSizes, xlarge);
          height: map-get($circleSizes, xlarge);
        }

        &__text {
          @include type-body-l;

          margin: rhythm(small) 0 0;
        }
      }

      circle {
        stroke-width: 8;
      }
    }
  }

  // Button version
  .zg-loader--button {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: inherit;
    z-index: 5;

    .circle-animation {
      stroke: color(black);
    }

    .zg-loader {
      &__text {
        display: none;
      }

      &--small {
        .zg-loader__circles {
          width: map-get($circleSizesButton, small);
          height: map-get($circleSizesButton, small);
        }
      }

      &--medium {
        .zg-loader__circles {
          width: map-get($circleSizesButton, medium);
          height: map-get($circleSizesButton, medium);
        }
      }

      &--large {
        .zg-loader__circles {
          width: map-get($circleSizesButton, medium);
          height: map-get($circleSizesButton, medium);
        }
      }

      &--xlarge {
        .zg-loader__circles {
          width: map-get($circleSizesButton, xlarge);
          height: map-get($circleSizesButton, xlarge);
        }
      }
    }
  }

  // Fullscreen version
  .zg-loader--fullscreen {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: color(black, 0.5);
    transition: all 0.2s ease;
    z-index: 100;

    .zg-loader__text {
      color: color(white);
      margin: rhythm() 0 0;
    }

    .circle-animation {
      stroke: color(white);
    }
  }

  // Boxed version
  .zg-loader--boxed {
    .zg-loader__box {
      min-width: 60%;
      max-width: 90%;
      background: color(white);
      border-radius: rhythm(small);
      padding: rhythm(medium);
      transition: all 0.3s ease;

      @include mq(medium) {
        min-width: 20%;
        max-width: 40%;
      }
    }

    .zg-loader__text {
      color: color(gray-500);
      margin: rhythm() 0 0;
      white-space: normal;
    }

    .circle-animation {
      stroke: color(brand-500);
    }
  }

  svg:not(:root) {
    overflow: visible;
  }

  @keyframes rotate {
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes circle {
    0% {
      stroke-dasharray: 1, 200;
      stroke-dashoffset: 0;
    }

    50% {
      stroke-dasharray: 89, 200;
      stroke-dashoffset: -35;
    }

    100% {
      stroke-dasharray: 89, 200;
      stroke-dashoffset: -142;
    }
  }

  @keyframes line {
    0% {
      width: 20%;
      left: -20%;
    }

    50% {
      width: 80%;
      left: 10%;
    }

    100% {
      width: 20%;
      left: 100%;
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.15s;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
</style>
